import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  Button,
  Chips,
  DataTable,
  Dialog,
  EmptyState,
  Field,
  Icon,
  Input,
  Pagination,
  Panel,
  Select,
  Stat,
  Tag,
  useErrorDialog,
  useToast,
  type Column,
} from '@dayon/design-system';
import { formatCount, formatPesos } from '@dayon/money';
import type { PageMeta, TransactionRow } from '@dayon/contracts';
import { api, ApiRequestError } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { ScreenHeader } from '../shell/ScreenHeader.tsx';
import { TransactionDetail } from './TransactionDetail.tsx';
import { SavedViewsDialog } from '../views/SavedViewsDialog.tsx';
import { ColumnPickerDialog } from '../views/ColumnPickerDialog.tsx';

/**
 * The unified ledger.
 *
 * "One ledger for deposits, withdrawals, bets, wins, bonuses and manual
 * adjustments — type is a filter, not a separate screen." Everything on this
 * page follows from that sentence: two independent chip rows over one table,
 * never a tab per money movement.
 */

/** The design fixes the page at 8 rows so the totals strip under the body
    describes something an operator can actually see at once. */
const PAGE_SIZE = 8;

type Kind = TransactionRow['kind'];
type Status = TransactionRow['status'];

const KIND_LABEL: Record<Kind, string> = {
  DEPOSIT: 'Deposit',
  WITHDRAWAL: 'Withdrawal',
  BET: 'Bet',
  WIN: 'Win',
  BONUS_GRANT: 'Bonus grant',
  /* The third bonus movement, and the only kind in this ledger with both sides
     on the same player: PLAYER_BONUS out, PLAYER_CASH in, when a wagering
     requirement is met. Its Amount column therefore reads ₱0.00 — the strip
     signs entries against the player's own accounts and nothing entered or left
     them — and that is the row telling the truth, not a gap in it. */
  BONUS_CONVERT: 'Bonus converted',
  BONUS_FORFEIT: 'Bonus forfeit',
  ADJUSTMENT: 'Adjustment',
  COMMISSION: 'Commission',
  REVERSAL: 'Reversal',
};

/**
 * The prototype's chip row showed only Deposit · Withdrawal · Bonus ·
 * Adjustment, but the stated rule includes bets and wins — and the API's `kind`
 * filter takes exactly one enum value, so a merged "Bonus" chip could not be
 * expressed as a request. The row is therefore the ledger's own vocabulary,
 * one chip per kind.
 */
const KIND_CHIPS: readonly { key: 'ALL' | Kind; label: string }[] = [
  { key: 'ALL', label: 'All' },
  ...(Object.keys(KIND_LABEL) as Kind[]).map((k) => ({ key: k, label: KIND_LABEL[k] })),
];

const STATUS_LABEL: Record<Status, string> = {
  SETTLED: 'Settled',
  PENDING: 'Pending',
  AWAITING_CHECKER: 'Awaiting checker',
  FAILED: 'Failed',
  REVERSED: 'Reversed',
};

const STATUS_CHIPS: readonly { key: 'ALL' | Status; label: string }[] = [
  { key: 'ALL', label: 'All' },
  ...(Object.keys(STATUS_LABEL) as Status[]).map((s) => ({ key: s, label: STATUS_LABEL[s] })),
];

/** Kinds that debit the player's balance. The prototype outlined only the
    Withdrawal chip; the rule behind it is the sign, so every debit is outlined. */
const DEBIT_KINDS = new Set<Kind>(['WITHDRAWAL', 'BET', 'BONUS_FORFEIT']);

/** "29 Aug, 21:14" — the design's stamp. 24-hour, because a cashier reading a
    ledger at 09:14 must not have to work out which nine o'clock it was. */
const stamp = new Intl.DateTimeFormat('en-PH', {
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/**
 * THE EXPORT'S THREE CHOICES USED TO BE NINE, AND EIGHT OF THEM WERE FICTION.
 *
 * The dialog offered XLSX and PDF against an engine that emits CSV, and offered
 * email and SFTP delivery against one that stores a file and serves it back.
 * "Selected rows only" had no row selection to read. Every one of those was a
 * promise the platform could not keep, on a form whose own notice said the
 * button did not work — which is how a fiction survives review.
 *
 * What is left is what is real: a window, the filters the engine can carry, and
 * a file you download. The notice now says what the file will and will not
 * contain rather than describing a job that does not exist.
 */
const EXPORT_SCOPES = ['Current view and filters', 'Whole module, no filters'];

const columns: Column<TransactionRow>[] = [
  {
    key: 'ref',
    header: 'Transaction',
    /* The one column the picker refuses to turn off: it is what says WHICH
       transaction a row is, and a grid of amounts belonging to no reference is
       not a ledger. */
    required: true,
    render: (t) => (
      <span className="tabular">{t.ref}</span>
    ),
  },
  {
    key: 'kind',
    header: 'Type',
    render: (t) => (
      <Tag tone={DEBIT_KINDS.has(t.kind) ? 'outline' : 'neutral'}>{KIND_LABEL[t.kind]}</Tag>
    ),
  },
  {
    key: 'amount',
    header: 'Amount',
    numeric: true,
    // The API already signs the amount against the PLAYER's balance, so the
    // leading + or − is read straight off the ledger rather than inferred from
    // the kind. The sign, not the colour, is what carries credit vs debit.
    render: (t) => {
      const value = BigInt(t.amount);
      return (
        <>
          <span
            className="tabular"
            style={value >= 0n ? { color: 'var(--portal-accent-text)' } : undefined}
          >
            {formatPesos(value, { sign: true })}
          </span>
          <div className="text-subtle" style={{ fontSize: '11px' }}>
            {stamp.format(new Date(t.occurredAt))}
          </div>
        </>
      );
    },
  },
  {
    key: 'status',
    header: 'Status',
    // "Failed and Reversed always carry a provider error code" — the database
    // CHECK enforces it, so the code is printed with the status rather than
    // hidden in a drawer: it is the first thing an operator needs.
    render: (t) => (
      <>
        <Tag
          tone={
            t.status === 'SETTLED'
              ? 'accent'
              : t.status === 'FAILED' || t.status === 'REVERSED'
                ? 'quiet'
                : 'outline'
          }
        >
          {STATUS_LABEL[t.status]}
        </Tag>
        {t.providerErrorCode ? (
          <div className="text-subtle tabular" style={{ fontSize: '11px' }}>
            {t.providerErrorCode}
          </div>
        ) : null}
      </>
    ),
  },
  {
    key: 'player',
    header: 'Player',
    render: (t) => (
      <>
        {t.playerName ?? <span className="text-subtle">no player</span>}
        <div className="text-subtle" style={{ fontSize: '11px' }}>
          {[t.playerRef, t.brandName].filter(Boolean).join(' · ') || '—'}
        </div>
      </>
    ),
  },
];

/** Guards for the two closed sets that can arrive in the URL. */
const isKind = (value: string): value is Kind => Object.hasOwn(KIND_LABEL, value);
const isStatus = (value: string): value is Status => Object.hasOwn(STATUS_LABEL, value);

/* ── The date window ──────────────────────────────────────────────────────
 *
 * `GET /api/transactions` has taken `from` and `to` since it was written, and
 * this screen never sent them: every ledger question asked here was answered
 * over ALL TIME, silently, under a heading that said nothing about it. "How
 * much did we take yesterday" and "how much have we ever taken" came back
 * looking identical.
 *
 * Two decisions turn a picked day into a bound the route accepts.
 *
 * WHICH MIDNIGHT. Manila's — not the browser's, not UTC. Every other financial
 * boundary in this platform is cut at Asia/Manila midnight by
 * `packages/db/src/gaming-day.ts`: the nightly rollup, the commission periods,
 * the dashboard's KPI window. That file exists because two writers once
 * disagreed about where the day ended and wrote two rows per gaming day into
 * `reporting.daily_metric`, doubling every sum over it. A ledger that answered
 * "30 August" with a UTC day would disagree with the rollup for the same 30
 * August by eight hours, and those two figures WILL be put beside each other.
 * So the boundary is Manila midnight — 16:00Z while the Philippines observes no
 * DST — derived through `Intl` rather than by subtracting a fixed eight hours,
 * exactly as `gamingDayStart` derives it.
 *
 * It is re-derived here rather than imported because the back office has no
 * dependency on `@dayon/db` (a server-side package that pulls in `pg` and
 * drizzle) and adding one to ship a filter is not a trade worth making. The
 * ZONE below is the single fact both copies turn on; if the platform is ever
 * relicensed, that constant and `GAMING_DAY_TIMEZONE` move together.
 *
 * WHICH END. The route's predicates are asymmetric and half-open:
 * `occurred_at >= $6` and `occurred_at < $7`. "To 31 August" means the operator
 * wants 31 August's rows, so the bound sent is the start of 1 SEPTEMBER — the
 * day AFTER the one asked for. Sending 31 August's own midnight would drop a
 * whole day of money and nothing on screen would say so.
 *
 * The URL carries the DAYS the operator picked (`from=2026-08-30`), not the
 * instants: the day is what was meant, the conversion is the rule above, and a
 * link carrying the day re-derives the same window on any machine in any zone.
 */
const GAMING_DAY_TIMEZONE = 'Asia/Manila';

/** How far the gaming-day zone runs ahead of UTC at a given instant, in ms. */
function zoneOffsetMs(at: Date): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: GAMING_DAY_TIMEZONE,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(at);
  const get = (type: string): number => Number(parts.find((p) => p.type === type)?.value ?? '0');
  // `hour` comes back as 24 at midnight under hour12:false in some engines.
  const local = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour') % 24,
    get('minute'),
    get('second'),
  );
  return local - at.getTime();
}

/** The instant Manila midnight OPENS the calendar day `day` (`YYYY-MM-DD`). */
function gamingDayStart(day: string): Date {
  const asIfUtc = new Date(`${day}T00:00:00Z`);
  return new Date(asIfUtc.getTime() - zoneOffsetMs(asIfUtc));
}

/** The calendar day after `day`. Stepped in UTC, which has no DST, so it is
    exact — the ZONE is applied afterwards by `gamingDayStart`. */
function nextDay(day: string): string {
  return new Date(Date.parse(`${day}T00:00:00Z`) + 86_400_000).toISOString().slice(0, 10);
}

/**
 * A real calendar day, in the shape `<input type="date">` speaks.
 *
 * Guarded the same way `kind` and `status` are, and for a sharper reason: a
 * bound that parses to an Invalid Date reaches the route as `null` — or, worse,
 * a plausible-looking `2026-02-31` rolls forward to 3 March — and a wrong window
 * returns rows that look like an answer. Anything that is not exactly a day this
 * calendar holds is dropped rather than guessed at.
 */
function isDay(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = Date.parse(`${value}T00:00:00Z`);
  return Number.isFinite(parsed) && new Date(parsed).toISOString().startsWith(value);
}

/** "30 Aug 2026". Formatted in UTC so the label names the day that was PICKED,
    whatever zone the operator's laptop is set to. */
const dayStamp = new Intl.DateTimeFormat('en-PH', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
});
const formatDay = (day: string): string => dayStamp.format(new Date(`${day}T00:00:00Z`));

/**
 * What the header says the ledger is answering over.
 *
 * There is no wording here for "unbounded" that leaves it unsaid. A ledger with
 * no window is answering over every date on record, and that has to be as
 * legible as any other window or an operator reads a lifetime total as a day's.
 */
function describeWindow(from: string, to: string): string {
  if (from && to) {
    return from === to ? `${formatDay(from)} only` : `${formatDay(from)} – ${formatDay(to)}`;
  }
  if (from) return `${formatDay(from)} onwards`;
  if (to) return `up to and including ${formatDay(to)}`;
  return 'all dates — no window set';
}

export function Transactions() {
  const { brandId, brands, setBrandId, allowed } = useSession();
  const { showError } = useErrorDialog();
  const { toast } = useToast();

  /*
   * THE FILTERS LIVE IN THE URL.
   *
   * This module imported no router hook at all, so `/transactions?playerId=…`
   * was read by nothing: the profile's "Open in Transactions" link landed on
   * the whole platform's ledger with the player silently dropped, and an
   * operator who followed it was looking at somebody else's rows under a
   * heading that said nothing about it. That is the worst kind of wrong screen
   * — it looks right.
   *
   * Reading them from here fixes the link and makes a filtered ledger
   * SHAREABLE, which is what the roster already does and what a support ticket
   * actually needs pasted into it.
   *
   * `playerRef` is carried alongside `playerId` purely so the banner can NAME
   * the player before any row has landed — and it is never sent to the API. The
   * filter is the id; the ref is a label, and if it is absent the banner falls
   * back to the ref on the first row and then to the id itself.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(() => searchParams.get('q') ?? '');
  const [kind, setKind] = useState<'ALL' | Kind>(() => {
    const raw = searchParams.get('kind');
    return raw && isKind(raw) ? raw : 'ALL';
  });
  const [status, setStatus] = useState<'ALL' | Status>(() => {
    const raw = searchParams.get('status');
    return raw && isStatus(raw) ? raw : 'ALL';
  });
  /* Gaming days, `YYYY-MM-DD`, exactly as the URL and the date inputs carry
     them. The conversion to the instants the route wants happens at the request,
     not here — see the date-window block above. */
  const [fromDay, setFromDay] = useState(() => {
    const raw = searchParams.get('from') ?? '';
    return isDay(raw) ? raw : '';
  });
  const [toDay, setToDay] = useState(() => {
    const raw = searchParams.get('to') ?? '';
    return isDay(raw) ? raw : '';
  });
  const [playerId, setPlayerId] = useState(() => searchParams.get('playerId') ?? '');
  const [playerRefHint, setPlayerRefHint] = useState(() => searchParams.get('playerRef') ?? '');
  const [page, setPage] = useState(() => {
    const raw = Number(searchParams.get('page'));
    return Number.isInteger(raw) && raw > 0 ? raw : 1;
  });
  /*
   * WHICH ROW IS OPEN, in the address bar like every other filter on this
   * screen.
   *
   * A ledger row is the thing an operator pastes into a ticket — "the posting
   * I mean is this one" — and a drawer whose state lives only in memory cannot
   * be pasted. `?tx=<id>` opens the detail on load, survives a reload, and
   * makes the reversal queue reachable by a link rather than by a description
   * of how to find the row.
   */
  const [openId, setOpenId] = useState<string | null>(() => searchParams.get('tx'));
  /* Bumped after a reversal lands, so the list behind the drawer re-reads
     itself: an approved reversal adds a REVERSAL row and the page it belongs on
     is this one. */
  const [reloadToken, setReloadToken] = useState(0);
  const [rows, setRows] = useState<TransactionRow[]>([]);
  const [meta, setMeta] = useState<PageMeta | null>(null);
  const [loading, setLoading] = useState(true);
  /**
   * Whether the last ledger read FAILED, as distinct from coming back empty.
   *
   * The catch already opened the error modal, but nothing recorded that the
   * request never landed — so the table behind the modal fell through to its
   * empty state and told the operator "Nothing matches the current brand scope
   * and filters", which is a claim about the LEDGER. Dismiss the modal, read
   * "No transactions for Bingo Barrio", and a brand that took money all day
   * looks like a brand that took none. "There are none" and "we could not ask"
   * are different facts, and on a money screen only one of them is safe to act
   * on — so from here they read differently.
   */
  const [loadFailed, setLoadFailed] = useState(false);
  const [dialog, setDialog] = useState<'views' | 'columns' | 'export' | null>(null);

  const [exportScope, setExportScope] = useState(EXPORT_SCOPES[0]!);
  const [exporting, setExporting] = useState(false);
  /** The finished file, so the dialog can offer it rather than a toast alone. */
  const [exported, setExported] = useState<{
    ref: string;
    rowCount: number;
    truncated: boolean;
    downloadPath: string;
  } | null>(null);

  /** Whether this role may take the ledger OFF the platform, as opposed to read it. */
  const canExport = allowed('audit.export');
  /**
   * Whether it may read the ledger at all, hoisted out of the refusal below
   * because the REFUSAL IS NOT THE ONLY PLACE THAT NEEDS IT. The early return
   * paints a notice instead of the screen; it does not stop the effects, which
   * were registered before it ran. The ledger read has to check for itself.
   */
  const canReadLedger = allowed('transactions.view');

  /**
   * The columns THIS admin has turned off, and the set the table then renders.
   *
   * Read once on mount rather than on every filter change: a column choice is a
   * preference, not data, and nothing else on the screen can move it.
   *
   * A FAILED READ SHOWS EVERY COLUMN. That is the safe direction — a column an
   * operator cannot see is a number they will not check — and it is why the
   * failure is swallowed here rather than raised into the error dialog: a
   * preference that could not be fetched is not worth interrupting somebody
   * over when the fallback is the complete table.
   */
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  useEffect(() => {
    let cancelled = false;
    api
      .get<{ hidden: string[] }>('/api/views/columns?screen=TRANSACTIONS')
      .then((d) => !cancelled && setHiddenColumns(d.hidden))
      .catch(() => !cancelled && setHiddenColumns([]));
    return () => {
      cancelled = true;
    };
  }, []);

  const visibleColumns = useMemo(
    /* `required` is honoured here as well as in the picker, so a choice stored
       by some other means cannot empty the table of its references. */
    () => columns.filter((c) => c.required === true || !hiddenColumns.includes(c.key)),
    [hiddenColumns],
  );

  /**
   * The export's OWN window, which the screen's may not supply.
   *
   * This table is happy with no window at all — the header says "all dates — no
   * window set" — and an export is not: the route needs two days, and picking
   * ninety of them on the operator's behalf would hand back a file that is not
   * the view they asked to export and says nothing about it. So the dialog asks,
   * pre-filled from the screen when the screen has one.
   */
  const [exportFrom, setExportFrom] = useState('');
  const [exportTo, setExportTo] = useState('');
  const [exportDateError, setExportDateError] = useState<string | undefined>(undefined);

  /**
   * RUN THE EXPORT.
   *
   * Synchronous, and that is not a compromise: the report engine caps a run at
   * MAX_ROWS and at a statement timeout, so the file either arrives or is
   * refused — there is nothing a queue would add except a place for the request
   * to sit. The dialog says the row cap out loud, because a truncated extract
   * that looks complete is the failure that matters on a regulatory file.
   */
  const runExport = useCallback(async () => {
    if (!exportFrom || !exportTo) {
      setExportDateError('Give the export a first and last day.');
      return;
    }
    if (exportFrom > exportTo) {
      setExportDateError('The range starts after it ends.');
      return;
    }
    setExportDateError(undefined);
    const whole = exportScope === EXPORT_SCOPES[1];
    setExporting(true);
    try {
      const result = await api.post<{
        ref: string;
        rowCount: number;
        truncated: boolean;
        downloadPath: string;
      }>('/api/transactions/export', {
        from: exportFrom,
        to: exportTo,
        ...(brandId ? { brandId } : {}),
        /* The filters ride only on "current view". The free-text search is NOT
           among them — the engine filters by equality so a bound value can never
           become syntax, and a LIKE across two columns is not that. The notice
           says so where an operator will read it before pressing. */
        ...(whole
          ? {}
          : {
              ...(kind !== 'ALL' ? { kind } : {}),
              ...(status !== 'ALL' ? { status } : {}),
              ...(playerId ? { playerId } : {}),
            }),
      });
      setExported(result);
      toast(
        result.truncated
          ? `${result.ref} is ready, and it is SHORT: the row cap stopped it at ${formatCount(result.rowCount)}. Narrow the window before you rely on it.`
          : `${result.ref} is ready — ${formatCount(result.rowCount)} ${result.rowCount === 1 ? 'row' : 'rows'}.`,
      );
    } catch (error: unknown) {
      showError({
        title: 'The export did not run',
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'No file was produced. Try again shortly.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setExporting(false);
    }
  }, [exportScope, exportFrom, exportTo, brandId, kind, status, playerId, showError, toast]);

  /* Pre-fill from the screen each time the dialog opens, and clear the last
     run: a file produced from different filters must not still be on offer
     under the new ones. */
  useEffect(() => {
    if (dialog !== 'export') return;
    setExportFrom(fromDay);
    setExportTo(toDay);
    setExportDateError(undefined);
    setExported(null);
  }, [dialog, fromDay, toDay]);

  /**
   * The brand scope is global and lives in the shell, so — unlike the search box
   * and the two chip rows, which reset the page as they change it — it can move
   * while the operator is standing on page 6.
   *
   * Narrowing to a brand almost always shrinks the result set, often to fewer
   * pages than the one they are on, and the ledger would then fetch an offset
   * past the end: an empty table under the heading "No transactions for Bingo
   * Barrio" for a brand with plenty of them, and no pager to climb back with,
   * because Pagination hides itself when a single page holds everything. An
   * operator reads that as a real zero. So the page returns to 1 whenever the
   * scope changes, which is what every other filter on this screen already does.
   */
  /*
   * Adjusted DURING RENDER rather than in an effect, and compared against the
   * previous scope rather than run unconditionally.
   *
   * As an effect it also fired on MOUNT, which was harmless while the page
   * always started at 1 — and stopped being harmless the moment the page came
   * from the URL: `/transactions?playerId=…&page=6` mounted, read page 6 out of
   * the address, and the effect immediately put it back to 1. A deep link to a
   * page other than the first silently opened the first, which is the quiet
   * kind of wrong that nobody reports.
   */
  const [scopeAtLastReset, setScopeAtLastReset] = useState(brandId);
  if (scopeAtLastReset !== brandId) {
    setScopeAtLastReset(brandId);
    setPage(1);
  }

  /**
   * A reversed window, said INLINE.
   *
   * This is a field-level typo — the operator set To first and then reached for
   * From — not a failed request, so it belongs beside the input that is wrong
   * and never in the error dialog. `useErrorDialog` on this screen means one
   * thing: the ledger read did not land.
   *
   * Compared as strings, which is sound only because both sides are zero-padded
   * `YYYY-MM-DD` and `isDay` has already refused anything else.
   */
  const rangeError =
    fromDay && toDay && fromDay > toDay
      ? 'From is after To, so the window is empty. Swap the two dates or clear one.'
      : null;

  /* ── URL sync ────────────────────────────────────────────────────────────
     Guarded by a string compare so the write that follows a read cannot loop.
     The brand scope is deliberately NOT written here: it is global, lives in
     the shell and persists across modules, so putting it in this module's query
     string would give one filter two homes that could disagree. */
  useEffect(() => {
    const next = new URLSearchParams();
    if (query.trim()) next.set('q', query.trim());
    if (kind !== 'ALL') next.set('kind', kind);
    if (status !== 'ALL') next.set('status', status);
    if (fromDay) next.set('from', fromDay);
    if (toDay) next.set('to', toDay);
    if (playerId) next.set('playerId', playerId);
    if (playerId && playerRefHint) next.set('playerRef', playerRefHint);
    if (page > 1) next.set('page', String(page));
    if (openId) next.set('tx', openId);
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [
    query,
    kind,
    status,
    fromDay,
    toDay,
    playerId,
    playerRefHint,
    page,
    openId,
    searchParams,
    setSearchParams,
  ]);

  useEffect(() => {
    /*
     * A reversed window is not ASKED, it is refused.
     *
     * `from > to` describes a window no transaction can fall in, so the honest
     * response is to stop — not to send it and let the route return an empty
     * page that the table would caption "Nothing matches the current brand
     * scope, date window and filters". That sentence is a claim about the
     * LEDGER, and the ledger was never consulted. The rows are cleared for the
     * same reason a failed read clears them: a stale page under a corrected
     * heading reads as the current one.
     */
    if (rangeError) {
      setRows([]);
      setMeta(null);
      setLoadFailed(false);
      setLoading(false);
      return;
    }
    /**
     * NOT ASKED, for the second reason: this role may not read the ledger.
     *
     * The module's refusal below is a RENDER decision and runs after every hook
     * on this screen has been registered, so without this the effect fired
     * anyway, was answered 403, and raised "Could not load the ledger" on top of
     * the notice explaining that the role does not hold `transactions.view`. One
     * screen making two claims, and they contradict each other.
     *
     * `loadFailed` stays FALSE, deliberately: nothing failed. The rows are empty
     * because they were never requested, which is what the notice says.
     */
    /**
     * NOT ASKED, for the second reason: this role may not read the ledger.
     *
     * The module's refusal below is a RENDER decision and runs after every hook
     * on this screen has been registered, so without this the effect fired
     * anyway, was answered 403, and raised "Could not load the ledger" on top of
     * the notice explaining that the role does not hold `transactions.view`. One
     * screen making two claims, and they contradict each other.
     *
     * `loadFailed` stays FALSE, deliberately: nothing failed. The rows are empty
     * because they were never requested, which is what the notice says.
     */
    if (!canReadLedger) {
      setRows([]);
      setMeta(null);
      setLoadFailed(false);
      setLoading(false);
      return;
    }
    let cancelled = false;
    // Debounced for the same reason the player list is: the ledger is the
    // largest table in the platform and a request per keystroke makes the rows
    // flicker between stale responses.
    const timer = setTimeout(() => {
      setLoading(true);
      const params = new URLSearchParams({ page: String(page), perPage: String(PAGE_SIZE) });
      if (query.trim()) params.set('q', query.trim());
      if (kind !== 'ALL') params.set('kind', kind);
      if (status !== 'ALL') params.set('status', status);
      if (playerId) params.set('playerId', playerId);
      // Days in, INSTANTS out. `from` is Manila midnight opening the day asked
      // for; `to` is Manila midnight opening the day AFTER it, because the
      // route's predicate is `occurred_at < $7`. Both ends of what the operator
      // picked are therefore inside the window, in full.
      if (fromDay) params.set('from', gamingDayStart(fromDay).toISOString());
      if (toDay) params.set('to', gamingDayStart(nextDay(toDay)).toISOString());
      if (brandId) params.set('brandId', brandId);

      api
        .get<{ items: TransactionRow[]; meta: PageMeta }>(`/api/transactions?${params}`)
        .then((d) => {
          if (cancelled) return;
          setRows(d.items);
          setMeta(d.meta);
          setLoadFailed(false);
        })
        .catch((error: unknown) => {
          if (cancelled) return;
          // Cleared rather than left standing. A stale page under a failure
          // notice reads as the current ledger, and the totals strip and the
          // pager would carry on describing a window nobody just fetched.
          setRows([]);
          setMeta(null);
          setLoadFailed(true);
          showError({
            title: 'Could not load the ledger',
            message:
              error instanceof ApiRequestError
                ? error.message
                : 'The transactions could not be fetched. Try again shortly.',
          });
        })
        .finally(() => !cancelled && setLoading(false));
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    canReadLedger,
    query,
    kind,
    status,
    fromDay,
    toDay,
    rangeError,
    playerId,
    page,
    brandId,
    reloadToken,
    showError,
  ]);

  /**
   * The totals strip.
   *
   * Computed from the rows actually on screen — the prototype's figures were
   * static, and the list endpoint returns no aggregate over the whole filter.
   * Labelling them "on this page" is the difference between a figure and a
   * guess; see the caption under the strip.
   */
  const totals = useMemo(() => {
    let deposits = 0n;
    let withdrawals = 0n;
    let bonus = 0n;
    let net = 0n;
    for (const r of rows) {
      const value = BigInt(r.amount);
      net += value;
      if (r.kind === 'DEPOSIT') deposits += value;
      // Shown as a magnitude, as the design shows it: the minus sign already
      // lives in "Net movement".
      else if (r.kind === 'WITHDRAWAL') withdrawals += value < 0n ? -value : value;
      // A forfeit claws a grant back, so it reduces the cost of the bonus.
      else if (r.kind === 'BONUS_GRANT' || r.kind === 'BONUS_FORFEIT') bonus += value;
    }
    return { deposits, withdrawals, bonus, net };
  }, [rows]);

  const brandName = brands.find((b) => b.id === brandId)?.name ?? null;
  const filtered =
    kind !== 'ALL' ||
    status !== 'ALL' ||
    query.trim().length > 0 ||
    playerId !== '' ||
    fromDay !== '' ||
    toDay !== '';

  /** What the header claims this screen is answering over. */
  const windowLabel = describeWindow(fromDay, toDay);

  /**
   * What to CALL the player this ledger is scoped to.
   *
   * The ref carried in the link, then the ref on a row that came back, then the
   * id. Never a name: the id is the filter and the ref is what support reads
   * out on a call — an id alone is unreadable, and a name is not needed to
   * identify an account.
   */
  const scopedPlayerLabel = playerId
    ? playerRefHint || rows[0]?.playerRef || playerId
    : null;

  /** The empty state's reset clears the GLOBAL brand scope too — a nil result
      is most often the brand filter, and leaving it set makes the reset a lie.
      It clears the player scope for the same reason. */
  const resetFilters = () => {
    setKind('ALL');
    setStatus('ALL');
    setQuery('');
    setFromDay('');
    setToDay('');
    setPlayerId('');
    setPlayerRefHint('');
    setPage(1);
    setBrandId(null);
  };

  /**
   * APPLY A SAVED VIEW — by setting the filters, not just the address bar.
   *
   * This used to be `setSearchParams(new URLSearchParams(q))`, with a comment
   * saying that drove "the state read-back below, so applying one moves every
   * chip at once". There is no read-back. Every filter on this screen is
   * `useState(() => searchParams.get(…))` — a LAZY INITIALISER, which runs once
   * when the screen mounts and never again. So opening a saved view rewrote the
   * URL and moved nothing: the same rows, the same chips, and the address bar
   * quietly disagreeing with both.
   *
   * REPLACE, NOT MERGE. A view is the whole filter set, so anything the view
   * does not name is cleared rather than left over from whatever was on screen
   * — a leftover filter with nothing saying it is still applied is how a view
   * comes to show the wrong rows under the right name. That is why this reads
   * every key rather than only the ones present.
   *
   * The page goes back to 1 for the same reason it does everywhere else on this
   * screen: page 12 of the previous filters is not page 12 of these.
   */
  const applyQuery = (q: string) => {
    const next = new URLSearchParams(q);
    const rawKind = next.get('kind');
    const rawStatus = next.get('status');
    const rawFrom = next.get('from') ?? '';
    const rawTo = next.get('to') ?? '';

    setQuery(next.get('q') ?? '');
    setKind(rawKind && isKind(rawKind) ? rawKind : 'ALL');
    setStatus(rawStatus && isStatus(rawStatus) ? rawStatus : 'ALL');
    setFromDay(isDay(rawFrom) ? rawFrom : '');
    setToDay(isDay(rawTo) ? rawTo : '');
    setPlayerId(next.get('playerId') ?? '');
    setPlayerRefHint(next.get('playerRef') ?? '');
    setPage(1);
    /* The address bar last, so a view is still a link somebody can paste. The
       same validation runs on both paths, so what is stored, what is shown and
       what is in the URL cannot drift apart. */
    setSearchParams(next);
  };

  const clearDates = () => {
    setFromDay('');
    setToDay('');
    setPage(1);
  };

  const clearPlayerScope = () => {
    setPlayerId('');
    setPlayerRefHint('');
    setPage(1);
  };

  if (!canReadLedger) {
    return (
      <EmptyState icon="lock-key" title="Transactions">
        This module needs the transactions.view capability. Your role does not hold it.
      </EmptyState>
    );
  }

  return (
    <>
      {/*
        THE SENTENCE UNDER THE NAME WAS ALREADY ON THIS SCREEN, TWO NOTICES
        LOWER, WHERE THE PERSON WHO NEEDED IT HAD ALREADY STARTED SCROLLING.

        This module opened with its name, a count line and three buttons, and
        then — below the player-scope notice, which on a laptop is often
        enough to push it under the fold — a paragraph beginning "One ledger
        for deposits, withdrawals, bets, wins, bonuses and manual adjustments
        — type is a filter, not a separate screen". That is the design's rule
        for this module, quoted again in the comment at the top of this file,
        and it is a good sentence. It answers "what is in this list", though,
        and the question somebody arriving here actually has is "am I in the
        right place for the job I was sent to do".

        So the sentence was split rather than either half thrown away. The
        purpose below says what the screen is FOR and who comes to it: support
        and cashiers chasing one transaction, and whoever has to put a wrong one
        right. Those are the two jobs the screen exists to serve, and the second
        is the one with consequences — the people who reverse money arrive
        through this page.

        The old sentence's other half, that every kind of movement lives in this
        one list and the Type chips narrow it instead of sending you somewhere
        else, stayed in the notice below and now points at the control it is
        about. So nothing was lost: the duplicated purpose is gone, both facts
        are still on screen, and neither is stated twice.

        The capability refusal a few lines above still renders its own empty
        state with no header, deliberately. Somebody who may not read the ledger
        should not be handed a header describing a ledger they cannot see —
        and both paths say "Transactions", which is the sidebar's word for this
        module and the name the browser walk looks the page up by.
      */}
      <ScreenHeader
        title="Transactions"
        purpose="The one place every movement of money is recorded, for support and cashiers chasing a single transaction and putting a wrong one right."
        /*
          The caption moves across exactly as it was, all three states and all
          four trailing clauses, because on a money screen every one of them is
          load-bearing.

          It is tri-state, and the first two states are both refusals to show a
          figure. "the last read failed" means the request did not land; "the
          date window is reversed" means it was never sent, because From is
          after To and no transaction can fall inside that window. In both cases
          the count that would have stood here was never taken, which is why
          neither prints a zero: "0 of 0 in view" on a ledger reads as "this
          player has no transactions", and somebody answers a customer with it.
          Only the third state is a measurement — and it measures the PAGE
          against the window, eight rows out of however many matched, which is
          the same reason the totals strip under the table has to say "in view"
          instead of implying a total for the period.

          The trailing clauses — newest first, the date window, the brand
          scope, and the one player when the screen is scoped to one — sit
          outside the three states because they are true of all of them. They
          answer "what am I even looking at", and a reader who does not know
          whether this is one brand or all of them, one player or everybody,
          cannot judge the figure standing next to it.
        */
        meta={
          <>
            {loadFailed
              ? 'not loaded — the last read failed'
              : rangeError
                ? 'not loaded — the date window is reversed'
                : meta
                  ? `${formatCount(rows.length)} of ${formatCount(meta.total)} in view`
                  : 'Loading…'}{' '}
            · newest first · {windowLabel} · {brandName ?? 'all brands'}
            {scopedPlayerLabel ? ` · one player: ${scopedPlayerLabel}` : ''}
          </>
        }
        /*
          All three screen-level buttons move here unchanged, and none of them is
          gated, which is worth saying out loud because this console normally
          hides a control a role may not use.

          Saved views and Columns only rearrange what is already on the screen,
          so they reveal nothing the table does not. Export is shown to every
          role that can read the ledger, and the dialog it opens is where the
          answer lives: a role that may read but not take a copy sees the window,
          the filters, and a sentence saying its role cannot run one and why
          reading the ledger and taking a copy of it are treated as different
          acts. A missing button would teach that operator the console is broken;
          the dialog teaches them who to ask.
        */
        actions={
          <>
            <Button onClick={() => setDialog('views')}>
              <Icon name="bookmark-simple" />
              Saved views
            </Button>
            <Button onClick={() => setDialog('columns')}>
              <Icon name="columns" />
              Columns
            </Button>
            <Button variant="primary" onClick={() => setDialog('export')}>
              <Icon name="export" />
              Export
            </Button>
          </>
        }
      />

      {scopedPlayerLabel && (
        <div className="notice notice-quiet" id="transactions-player-scope">
          <Icon name="user-circle" />
          <div>
            Showing <strong>one player</strong>: {scopedPlayerLabel}. The filter is applied to
            the whole ledger, not just to this page — so the pager, the chips and the totals below
            all describe this player&rsquo;s transactions and nobody else&rsquo;s. This address is shareable as it stands.{' '}
            <Button small variant="ghost" onClick={clearPlayerScope}>
              Show every player
            </Button>
          </div>
        </div>
      )}

      <div className="notice notice-quiet">
        <Icon name="info" />
        <div>
          {/* The sentence that used to open this notice — "One ledger for
              deposits, withdrawals, bets, wins, bonuses and manual adjustments
              — type is a filter, not a separate screen" — was this
              screen's purpose, and it is now in the header above, where it is
              read before the figures rather than after them.

              What is left of it here is the half that is advice about a
              control, and it names the control. "Type is a filter" invites the
              other reading — that a filter for it lives somewhere else on
              the screen — and a reader hunting through this page for a
              control that is not on it is exactly the failure the
              advice-names-a-real-control rule was written about. The Type chip
              row is directly below, so that is what the sentence points at. */}
          Deposits, withdrawals, bets, wins, bonuses and manual adjustments are all in this one
          list: the <strong>Type</strong> chips below narrow it rather than taking you to another
          screen. <strong>Open a row</strong> for its entries, the account
          each side moved, whoever caused it, and the reversal relationship in both directions — and
          to reverse a mis-posted movement, which writes a NEW posting mirroring the original rather
          than editing anything. Amounts are signed against the player&rsquo;s balance:
          deposits and wins credit, bets and withdrawals debit. Failed and Reversed always carry a
          provider error code, printed under the status. The type, status, search, player,{' '}
          <strong>date window</strong> and page are all carried in the address bar, so a filtered
          ledger pastes into a ticket and opens the same view. Dates are gaming days, cut at
          Asia/Manila midnight — the same boundary the nightly daily totals and the commission
          periods use — and both ends are included, so 30–31 August covers both days in full.
        </div>
      </div>

      <div className="stack">
        <div className="search-field" style={{ width: '100%', maxWidth: '420px' }}>
          <Icon name="magnifying-glass" />
          <Input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(1);
            }}
            // The design asked for "Transaction ID, player, rail or provider
            // ref". The endpoint matches the transaction ref and the player ref
            // and nothing else, so the placeholder says only what is true — an
            // operator who searches a name, finds nothing and concludes the
            // transaction does not exist is the failure this avoids.
            placeholder="Transaction ID or player ID"
            aria-label="Search transactions by transaction ID or player ID"
          />
        </div>

        <div className="row">
          <span className="panel-title">Type</span>
          <Chips
            options={KIND_CHIPS}
            active={kind}
            onChange={(k) => {
              setKind(k);
              setPage(1);
            }}
            label="Filter by transaction type"
          />
        </div>

        <div className="row">
          <span className="panel-title">Status</span>
          <Chips
            options={STATUS_CHIPS}
            active={status}
            onChange={(s) => {
              setStatus(s);
              setPage(1);
            }}
            label="Filter by transaction status"
          />
        </div>

        {/* The two bounds sit in a wrapping row so they fall under one another
            on a phone rather than shrinking below a usable width; `.input`
            already carries the 44px floor on a coarse pointer. Each hint names
            the bound it belongs to — and is wired to that input through
            aria-describedby — because "included" and "excluded" is the whole
            question an operator has about a date filter on a money screen. */}
        <div className="row">
          <span className="panel-title">Dates</span>
          <div className="row" style={{ flex: '1 1 320px', maxWidth: '520px' }}>
            <div style={{ flex: '1 1 150px', minWidth: '145px' }}>
              <Field
                label="From"
                error={rangeError ?? undefined}
                hint="From Manila midnight opening this day."
              >
                {({ id, describedBy, invalid }) => (
                  <Input
                    id={id}
                    type="date"
                    value={fromDay}
                    max={toDay || undefined}
                    aria-describedby={describedBy}
                    aria-invalid={invalid}
                    onChange={(e) => {
                      setFromDay(e.target.value);
                      setPage(1);
                    }}
                  />
                )}
              </Field>
            </div>
            <div style={{ flex: '1 1 150px', minWidth: '145px' }}>
              <Field label="To" hint="This whole day, up to the next Manila midnight.">
                {({ id, describedBy }) => (
                  <Input
                    id={id}
                    type="date"
                    value={toDay}
                    min={fromDay || undefined}
                    aria-describedby={describedBy}
                    aria-invalid={rangeError !== null}
                    onChange={(e) => {
                      setToDay(e.target.value);
                      setPage(1);
                    }}
                  />
                )}
              </Field>
            </div>
            {(fromDay || toDay) && (
              <Button small onClick={clearDates}>
                <Icon name="calendar-slash" />
                Clear dates
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="notice notice-quiet">
        <Icon name="warning-circle" />
        <div>
          <strong>What this list cannot show yet.</strong> Each row arrives with the
          transaction ref, kind, status, provider error code, signed amount, player and brand —
          nothing else. So the chips carry no counts (nothing counts the rows per chip),
          the <em>Rail / source</em> column is left out rather than filled with dashes, and the
          order is fixed to newest first, so Amount is not sortable here. The rail, the actor and
          the entries all exist — opening a transaction resolves them across{' '}
          deposits,{' '}
          withdrawals,{' '}
          bonus grants,{' '}
          manual adjustments and{' '}
          reversals — so they are one click away and
          not in this table. <strong>A REVERSED ROW LOOKS ORDINARY HERE</strong>, and that is a
          real gap: the ledger is append-only, so reversing a posting leaves the original exactly
          as it was and records the correction as a link on the NEW row. The list does not read
          that link, so a settled deposit that has been reversed is still captioned Settled until
          you open it. Showing it in the table would mean carrying that link on the list itself,
          which is a change nobody has made yet.
        </div>
      </div>

      <Panel>
        {loading && rows.length === 0 ? (
          <div className="skeleton" style={{ height: '240px' }}>
            &nbsp;
          </div>
        ) : (
          <DataTable
            caption="Ledger transactions, newest first, with type, signed amount, status and player"
            columns={visibleColumns}
            rows={rows}
            rowKey={(t) => t.id}
            /* A row OPENS. Until this existed the ledger was a list nothing
               could be asked about — the entries, the actor and the reversal
               relationship all live one level down, and there was no level
               down. `DataTable` gives a clicked row a button role and keyboard
               activation, so this is reachable without a pointer. */
            onRowClick={(t) => setOpenId(t.id)}
            empty={
              <EmptyState
                icon={
                  loadFailed ? 'warning-circle' : rangeError ? 'calendar-slash' : 'arrows-left-right'
                }
                title={
                  loadFailed
                    ? 'The ledger could not be read'
                    : rangeError
                      ? 'The date window is reversed'
                      : scopedPlayerLabel
                        ? `No transactions for ${scopedPlayerLabel}`
                        : `No transactions for ${brandName ?? 'this filter'}`
                }
                action={
                  filtered || brandId ? (
                    <Button onClick={resetFilters}>Reset filters</Button>
                  ) : undefined
                }
              >
                {/* The failure branch comes first and never says "nothing
                    matches": an empty table under a failed read is a table that
                    was never filled, not a filter that found nothing. */}
                {loadFailed
                  ? 'This table is empty because the request failed, not because the ledger holds nothing under these filters. Nothing here is a count of anything. Try again shortly.'
                  : rangeError
                    ? 'From is after To, so no transaction can fall inside the window. The ledger was not asked and nothing here is a count of anything. Fix the dates above.'
                    : 'Nothing matches the current brand scope, date window and filters.'}
              </EmptyState>
            }
          />
        )}

        {rows.length > 0 && (
          <>
            {/* Pinned under the body, before the pager, exactly as the design
                places it: the reader's eye leaves the last row and lands on
                what those rows add up to. */}
            <div className="stat-grid" style={{ marginTop: 'var(--space-4)' }}>
              <Stat label="Deposits in view" value={formatPesos(totals.deposits)} />
              <Stat label="Withdrawals in view" value={formatPesos(totals.withdrawals)} />
              <Stat label="Bonus cost" value={formatPesos(totals.bonus)} />
              <Stat label="Net movement" value={formatPesos(totals.net, { sign: true })} />
            </div>
            <div className="text-subtle" style={{ fontSize: '11px', marginTop: 'var(--space-2)' }}>
              &ldquo;In view&rdquo; is the {rows.length} row{rows.length === 1 ? '' : 's'} on this
              page. No total across the whole filter comes back with the list, so these are summed
              from what is on screen — not the total for {windowLabel}.
            </div>
          </>
        )}

        {meta && (
          <Pagination page={meta.page} perPage={meta.perPage} total={meta.total} onPage={setPage} />
        )}
      </Panel>

      {/* ── Reconciliation ──────────────────────────────────────────────────
          "Reconciliation compares platform totals to provider settlement files;
          unmatched rows surface in the variance card." Nothing in the schema
          models a settlement file, an import run or a variance line, so this
          card names the gap instead of printing a ₱0 variance — which is the
          one figure on this screen that an operator would read as "we balance"
          and stop looking. */}
      <Panel title="Reconciliation · yesterday">
        <EmptyState icon="scales" title="No settlement data to reconcile against">
          Reconciliation compares platform totals per rail to the provider&rsquo;s settlement file.
          The platform side exists; the provider side does not — the platform does not take in
          providers&rsquo; settlement files or run a reconciliation yet, so there is nothing to compare
          and no variance to report. A ₱0 variance here would read as &ldquo;we balance&rdquo;, and
          that is not something this screen can currently know.
        </EmptyState>
      </Panel>

      {/* ── One row, opened ─────────────────────────────────────────────
          The detail is a DRAWER rather than a dialog because it is a place to
          read from — entries, actor, both ends of the reversal link — while the
          list it came from stays where it was. The reversal controls live
          inside it, and each one that is disabled says why in text beside it.
          Errors from any of its writes open the platform's error modal; nothing
          here reports a failure inline. */}
      <TransactionDetail
        transactionId={openId}
        onClose={() => setOpenId(null)}
        onChanged={() => setReloadToken((n) => n + 1)}
      />

      {/* ── Saved views ──────────────────────────────────────────────────
          The dialog is shared across screens and knows nothing about what these
          filters mean: it stores the query string this module already keeps in
          the address bar, and hands one back to be applied. Turning that string
          back into filters is this screen's job, because this screen is the only
          thing that knows what they are. */}
      <SavedViewsDialog
        open={dialog === 'views'}
        screen="TRANSACTIONS"
        screenLabel="Transactions"
        currentQuery={searchParams.toString()}
        onClose={() => setDialog(null)}
        onApply={applyQuery}
      />

      {/* ── Columns ─────────────────────────────────────────────────────── */}
      <ColumnPickerDialog
        open={dialog === 'columns'}
        screen="TRANSACTIONS"
        screenLabel="Transactions"
        columns={columns}
        hidden={hiddenColumns}
        onClose={() => setDialog(null)}
        onChanged={setHiddenColumns}
      />

      {/* ── Export ──────────────────────────────────────────────────────── */}
      <Dialog
        open={dialog === 'export'}
        onClose={() => setDialog(null)}
        title="Export"
        wide
        actions={
          <>
            <Button onClick={() => setDialog(null)}>Close</Button>
            {canExport ? (
              <Button
                variant="primary"
                busy={exporting}
                disabled={exporting}
                onClick={() => void runExport()}
              >
                {exported ? 'Run it again' : 'Create the file'}
              </Button>
            ) : null}
          </>
        }
      >
        <div className="stack">
          <div className="notice notice-quiet">
            <Icon name="download-simple" />
            <div>
              The file is <strong>CSV</strong>, it is built when you press the button, and you
              download it here. It is not emailed and there is no queue to wait in — the report
              engine runs it with read-only access, caps it, and stores the result so the
              same bytes can be fetched again later from <strong>Reports</strong>.
            </div>
          </div>

          <div className="halves">
            <Field label="First day" {...(exportDateError ? { error: exportDateError } : {})}>
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  type="date"
                  value={exportFrom}
                  aria-describedby={describedBy}
                  aria-invalid={invalid || undefined}
                  onChange={(e) => setExportFrom(e.target.value)}
                />
              )}
            </Field>
            <Field label="Last day" hint="Inclusive, on the Asia/Manila gaming day.">
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  type="date"
                  value={exportTo}
                  aria-describedby={describedBy}
                  onChange={(e) => setExportTo(e.target.value)}
                />
              )}
            </Field>
          </div>

          <Field
            label="Which rows"
            hint={
              exportScope === EXPORT_SCOPES[1]
                ? 'Every transaction in the window, in your brand scope.'
                : 'The type, status and player filters from the screen behind this dialog.'
            }
          >
            {({ id }) => (
              <Select id={id} value={exportScope} onChange={(e) => setExportScope(e.target.value)}>
                {EXPORT_SCOPES.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          {/*
            * The one filter that does NOT travel, said before the press rather
            * than discovered in the file.
            *
            * ── TWO THINGS THIS SENTENCE USED TO GET WRONG ────────────────────
            *
            * It ended "Filter by player instead if you need one player's rows",
            * and THERE IS NO PLAYER FILTER ON THIS SCREEN. `playerId` is state
            * this module only ever reads from the URL — `setPlayerId` is called
            * by the two reset paths and by applying a saved view, and by no
            * control. The scope arrives from Players ("Open the full ledger for
            * PL-…") or from a profile's Transactions tab ("Open in
            * Transactions"). An operator following that advice went looking
            * through the filter panel for something that was never there.
            *
            * And it explained itself with "the engine matches filters by exact
            * value — which is what stops one becoming part of the query". That
            * is a sentence about SQL injection, addressed to somebody deciding
            * whether to press a button. The reason they need is that a filter
            * matches a whole value and a search matches part of one; the
            * security property behind it is real and belongs here, in a comment.
            */}
          {exportScope === EXPORT_SCOPES[0] && query.trim() ? (
            <div className="notice">
              <Icon name="magnifying-glass" />
              <div>
                <strong>Your search for &ldquo;{query.trim()}&rdquo; will not be applied.</strong>{' '}
                The file will carry the type and status you picked above, and the one player if this
                screen is already showing one. It cannot carry the search box: those filters match a
                whole value, and a search matches part of one.
                {playerId ? (
                  <>
                    {' '}
                    This screen is scoped to <strong>{scopedPlayerLabel}</strong>, so the file will
                    be that player&rsquo;s rows either way.
                  </>
                ) : (
                  <>
                    {' '}
                    For one player, open them in <strong>Players</strong> and press{' '}
                    <strong>Open the full ledger</strong> &mdash; that brings you back here scoped to
                    them, and the export follows the scope.
                  </>
                )}
              </div>
            </div>
          ) : null}

          {exported ? (
            <div className="notice">
              <Icon name={exported.truncated ? 'warning-circle' : 'check-circle'} />
              <div>
                <strong>{exported.ref}</strong> · {formatCount(exported.rowCount)}{' '}
                {exported.rowCount === 1 ? 'row' : 'rows'}.
                {exported.truncated ? (
                  <>
                    {' '}
                    <strong>This file is short.</strong> The row cap stopped it before the end of
                    the answer, so it is not the whole window — narrow the dates and run it again
                    before you send it anywhere.
                  </>
                ) : null}
                <div style={{ marginTop: 'var(--space-2)' }}>
                  <a className="btn btn-secondary" href={exported.downloadPath} download>
                    Download {exported.ref}.csv
                  </a>
                </div>
              </div>
            </div>
          ) : null}

          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              <strong>The file carries the player reference, never a name.</strong> Taking the
              ledger off the platform is recorded: when it ran, who asked for it, the window, the
              filters and how many rows came out. The file is kept, so if anybody asks what was
              sent, the same file can be shown again rather than a fresh one that might not match.
              {canExport ? null : (
                <>
                  {' '}
                  Your role cannot run one — that needs{' '}
                  <span className="signin-perm">audit.export</span>, which is a different key from
                  the one that opens this screen, because reading the ledger and taking a copy of it
                  are different acts.
                </>
              )}
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
}
